import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Modal, Card, Table } from 'react-bootstrap';
import { numberFormatter } from '../../../components/formatters/numberFormatter';
import ReactToPrint from 'react-to-print';
import { DataContext } from '../../../init/getData';
import Loader from '../../../components/loader/loader';
import { FcPortraitMode, FcAbout, FcInTransit, FcHome } from "react-icons/fc";

const TrialBalanceReport = (props) => {
  const { api, userData, selectedCompany } = useContext(DataContext);
  const { showModal, toggleModal, filter, currency, mainCurrency, secondaryCurrency, data } = props;
  
  const reportRef = useRef(null);
  const [loading, setLoading] = useState(false);

  // Calculate totals
  const totals = useMemo(() => {
    if (!data?.data) return {};

    const isMainCurrency = currency === 'main';
    
    const totalOpeningDebit = data.data.reduce((sum, item) => 
      sum + (parseFloat(isMainCurrency ? item.totalDebitOpening : item.secondaryTotalDebitOpening) || 0), 0);
    
    const totalOpeningCredit = data.data.reduce((sum, item) => 
      sum + (parseFloat(isMainCurrency ? item.totalCreditOpening : item.secondaryTotalCreditOpening) || 0), 0);
    
    const totalDebit = data.data.reduce((sum, item) => 
      sum + (parseFloat(isMainCurrency ? item.totalDebitNormal : item.secondaryTotalDebitNormal) || 0), 0);
    
    const totalCredit = data.data.reduce((sum, item) => 
      sum + (parseFloat(isMainCurrency ? item.totalCreditNormal : item.secondaryTotalCreditNormal) || 0), 0);
    
    const totalBalance = data.data.reduce((sum, item) => 
      sum + (parseFloat(isMainCurrency ? item.balance : item.secondaryBalance) || 0), 0);

    return {
      totalOpeningDebit,
      totalOpeningCredit,
      totalDebit,
      totalCredit,
      totalBalance
    };
  }, [data, currency]);

  // Format account ID display
  const formatAccountId = (item) => {
    if (item.parentId && item.isAux) {
      return `${item.parent?.accountId}.${item.accountId}`;
    } else {
      return item.accountId;
    }
  };

  // Get current currency symbol
  const currentCurrency = currency === 'main' ? mainCurrency : secondaryCurrency;

  // Calculate if data would span multiple pages (rough estimate)
  const itemsPerPage = 20; // Adjust based on your font size and page size
  const hasMultiplePages = data?.data?.length > itemsPerPage;

  return (
    <Modal show={showModal} backdrop="static" size='xl' onHide={toggleModal}>
      <Modal.Header style={{ background: "#39aaa5", display: "flex", justifyContent: "center" }}>
        <div>
          <Modal.Title style={{ color: "white" }}>Trial Balance Report</Modal.Title>
        </div>
      </Modal.Header>
      
      <Modal.Body>
        <Card ref={reportRef} style={{ padding: '20px' }}>
          {/* CSS for print */}
          <style>
            {`
              @media print {
                /* Ensure totals only appear on last page */
                .summary-section, 
                .totals-footer,
                .print-footer {
                  page-break-before: always;
                }
                
                /* Keep header on each page */
                thead {
                  display: table-header-group;
                }
                
                /* Keep footer on last page */
                tfoot {
                  display: table-footer-group;
                }
                
                /* Remove modal padding for print */
                .modal-content {
                  border: none;
                  box-shadow: none;
                }
                
                /* Better page breaks for table rows */
                tr {
                  page-break-inside: avoid;
                }
                
                /* Hide non-print elements */
                .no-print {
                  display: none !important;
                }
              }
            `}
          </style>
          
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <h2 style={{ fontWeight: 'bold', color: '#000000' }}>TRIAL BALANCE</h2>
            <h4 style={{ color: '#000000' }}>{selectedCompany?.name}</h4>
            <div style={{ marginTop: '10px', color: '#000000' }}>
              <strong style={{ color: '#000000' }}>Period:</strong> {filter.fromDate} to {filter.toDate}
              <span style={{ marginLeft: '20px' }}>
                <strong style={{ color: '#000000' }}>Currency:</strong> {currentCurrency?.code}
              </span>
            </div>
          </div>

          {/* Simple horizontal line instead of Divider */}
          <hr style={{ marginBottom: '20px', borderTop: '2px solid #000000' }} />

          {/* Trial Balance Table */}
          <Table bordered hover style={{ fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ textAlign: 'center', padding: '10px', color: '#000000' }}>Account ID</th>
                <th style={{ padding: '10px', color: '#000000' }}>Type</th>
                <th style={{ padding: '10px', color: '#000000' }}>Account Name</th>
                <th style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>Opening Debit</th>
                <th style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>Opening Credit</th>
                <th style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>Debit</th>
                <th style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>Credit</th>
                <th style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>Balance</th>
              </tr>
            </thead>
            
            <tbody>
              {data?.data?.map((item, index) => (
                <tr key={item.id || index}>
                  <td style={{ textAlign: 'center', padding: '8px', color: '#000000' }}>
                    {formatAccountId(item)}
                  </td>
                  <td style={{ padding: '8px', color: '#000000' }}>{item.type}</td>
                  <td style={{ padding: '8px', color: '#000000' }}>{item.english}</td>
                  <td style={{ textAlign: 'right', padding: '8px', color: '#000000' }}>
                    {numberFormatter(currency === 'main' ? item.totalDebitOpening : item.secondaryTotalDebitOpening)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', color: '#000000' }}>
                    {numberFormatter(currency === 'main' ? item.totalCreditOpening : item.secondaryTotalCreditOpening)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', color: '#000000' }}>
                    {numberFormatter(currency === 'main' ? item.totalDebitNormal : item.secondaryTotalDebitNormal)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '8px', color: '#000000' }}>
                    {numberFormatter(currency === 'main' ? item.totalCreditNormal : item.secondaryTotalCreditNormal)}
                  </td>
                  <td style={{ 
                    textAlign: 'right', 
                    padding: '8px',
                    color: '#000000'
                  }}>
                    {numberFormatter(currency === 'main' ? item.balance : item.secondaryBalance)}
                  </td>
                </tr>
              ))}
            </tbody>

            {/* Totals row - will only print on last page */}
            {hasMultiplePages ? (
              <tfoot className="totals-footer" style={{ borderTop: '2px solid #000000' }}>
                <tr style={{ fontWeight: 'bold' }}>
                  <td colSpan="3" style={{ padding: '10px', color: '#000000' }}>TOTAL</td>
                  <td style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>
                    {numberFormatter(totals.totalOpeningDebit)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>
                    {numberFormatter(totals.totalOpeningCredit)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>
                    {numberFormatter(totals.totalDebit)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>
                    {numberFormatter(totals.totalCredit)}
                  </td>
                  <td style={{ 
                    textAlign: 'right', 
                    padding: '10px',
                    color: '#000000'
                  }}>
                    {numberFormatter(totals.totalBalance)}
                  </td>
                </tr>
              </tfoot>
            ) : (
              <tfoot style={{ borderTop: '2px solid #000000' }}>
                <tr style={{ fontWeight: 'bold' }}>
                  <td colSpan="3" style={{ padding: '10px', color: '#000000' }}>TOTAL</td>
                  <td style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>
                    {numberFormatter(totals.totalOpeningDebit)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>
                    {numberFormatter(totals.totalOpeningCredit)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>
                    {numberFormatter(totals.totalDebit)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '10px', color: '#000000' }}>
                    {numberFormatter(totals.totalCredit)}
                  </td>
                  <td style={{ 
                    textAlign: 'right', 
                    padding: '10px',
                    color: '#000000'
                  }}>
                    {numberFormatter(totals.totalBalance)}
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>

          {/* Summary Section - will only print on last page */}
          <div className={`summary-section ${hasMultiplePages ? 'page-break-before' : ''}`} 
               style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
            <h5 style={{ marginBottom: '15px', textDecoration: 'underline', color: '#000000' }}>Summary</h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', color: '#000000' }}>
              <div>
                <strong style={{ color: '#000000' }}>Total Opening Debit:</strong> {numberFormatter(totals.totalOpeningDebit)} {currentCurrency?.code}
              </div>
              <div>
                <strong style={{ color: '#000000' }}>Total Opening Credit:</strong> {numberFormatter(totals.totalOpeningCredit)} {currentCurrency?.code}
              </div>
              <div>
                <strong style={{ color: '#000000' }}>Total Debit:</strong> {numberFormatter(totals.totalDebit)} {currentCurrency?.code}
              </div>
              <div>
                <strong style={{ color: '#000000' }}>Total Credit:</strong> {numberFormatter(totals.totalCredit)} {currentCurrency?.code}
              </div>
              <div style={{ gridColumn: 'span 2', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #000000' }}>
                <strong style={{ color: '#000000' }}>Net Balance:</strong> 
                <span style={{
                  color: '#000000',
                  marginLeft: '10px',
                  fontSize: '16px'
                }}>
                  {numberFormatter(totals.totalBalance)} {currentCurrency?.code}
                </span>
              </div>
            </div>
          </div>

          {/* Footer - will only print on last page */}
          <div className={`print-footer ${hasMultiplePages ? 'page-break-before' : ''}`} 
               style={{ 
                 marginTop: '40px', 
                 textAlign: 'right', 
                 fontSize: '12px', 
                 color: '#000000',
                 paddingTop: '10px',
                 borderTop: '1px solid #000000'
               }}>
            <div>Generated on: {new Date().toLocaleDateString()}</div>
            <div>Generated by: {userData.name}</div>
          </div>
        </Card>
      </Modal.Body>

      <Modal.Footer className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            type="button" 
            className="btn btn-secondary" 
            style={{ backgroundColor: '#1f2937' }} 
            onClick={toggleModal}
          >
            Close
          </button>
          
          <ReactToPrint
            trigger={() => (
              <button 
                type="button" 
                style={{ backgroundColor: '#1d7790' }} 
                className="btn btn-primary"
              >
                Print
              </button>
            )}
            content={() => reportRef.current}
            documentTitle={`Trial Balance ${new Date().toLocaleDateString()}`}
            pageStyle={`
              @page {
                margin: 20mm;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                }
                .page-break {
                  page-break-before: always;
                }
                .avoid-break {
                  page-break-inside: avoid;
                }
                .always-break {
                  page-break-before: always;
                }
              }
            `}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default TrialBalanceReport;